import axios from "axios";
import React, { useState } from "react";
import SearchableDropdown from "../../components/Feedback/SearchableDropdown";
import { CheckCircleIcon } from "@heroicons/react/20/solid";
import { Link } from "react-router-dom";
import { BASE_URL2 } from "../../utils/cookies.config";
import Button from "../../components/Button";
import tapngoLogo from "../../assets/tapngo.svg";
import {
  AdinkraBackground,
  MonumentSilhouette,
} from "../../components/SignupArt/SignupArt";

const client = axios.create({
  baseURL: BASE_URL2,
});

const FeedbackForm = () => {
  const [formData, setFormData] = useState({
    serviceType: "",
    otherService: "",
    complaintCategory: "",
    complaintType: "",
    otherComplaint: "",
    travelTime: "",
    contactMethod: "",
    contactDetails: "",
  });

  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Predefined options
  const complaintCategories = {
    card: {
      label: "Tap n Go Card Issues",
      options: ["Card not working", "Balance issues", "Card damaged", "Failed top-up", "Card registration issues"],
    },
    app: {
      label: "App Issues",
      options: ["App crash", "Payment failure", "Login problems", "Booking issues", "Route tracking issues"],
    },
    service: {
      label: "Service Quality",
      options: ["Driver behavior", "Vehicle condition", "Route deviation", "Schedule delays", "Safety concerns"],
    },
    other: {
      label: "Other Issues",
      options: ["Other"],
    },
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.serviceType || !formData.complaintCategory) {
      setError("Please fill in all required fields");
      return;
    }
    // Here you would typically send the data to your backend
    const payload = {
      ...formData,
      from,
      to,
      contactMethod: formData.contactMethod === "no contact needed" ? "none" : formData.contactMethod,
      complaintCategory: complaintCategories[formData.complaintCategory].label,
    };

    setIsLoading(true);
    try {
      const res = await client.post("/feedback", payload);
      setSubmitted(true);
      setError("");
      setIsLoading(false);
      window.scrollTo(0, 0);
    } catch (e) {
      console.log(e);
      setError(e?.response?.data);
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const inputClass =
    "w-full rounded-lg border border-secondary px-4 py-3 focus:outline-none focus:ring-2 focus:ring-go";
  const labelClass = "block text-sm text-subtext mb-1";

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Brand panel — same treatment as the /signup page */}
      <div
        className="relative overflow-hidden text-white lg:flex lg:w-2/5"
        style={{
          background:
            "linear-gradient(to top, #0A3F28 0%, #12724A 55%, #1C9A62 100%)",
        }}
      >
        <MonumentSilhouette className="absolute inset-x-0 top-0 bottom-14 sm:bottom-16 lg:top-auto lg:bottom-0 lg:h-3/5" />

        <div className="relative z-10 flex flex-col text-center px-6 pt-14 pb-28 lg:h-full lg:justify-center lg:text-left lg:px-12 xl:px-16 lg:py-16">
          <img
            src={tapngoLogo}
            alt=""
            className="h-10 w-10 lg:h-12 lg:w-12 rounded-xl shadow-lg mx-auto mb-5 lg:mx-0 lg:mb-10"
          />
          <h2 className="text-xl lg:text-3xl xl:text-4xl font-bold leading-tight mb-2 lg:mb-4 max-w-xs mx-auto lg:max-w-sm lg:mx-0 text-white">
            Tell us how your ride went.
          </h2>
          <p className="text-sm lg:text-base text-white opacity-80 max-w-xs mx-auto lg:mx-0">
            Report a problem with your card, the app, or a trip, and help us
            make every ride in Ghana better.
          </p>
        </div>
      </div>

      {/* Form panel — rounded sheet over the hero on mobile, like /signup */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-start lg:justify-center bg-secondary-surface rounded-t-3xl -mt-14 sm:-mt-16 lg:mt-0 lg:rounded-none px-4 pt-10 pb-10 lg:py-16">
        <AdinkraBackground className="absolute inset-0 overflow-hidden rounded-t-3xl lg:rounded-none" />
        <div className="relative w-full max-w-xl">
          <div className="bg-white rounded-2xl shadow-xl border border-secondary px-5 py-7 sm:px-10 sm:py-10">
            {submitted ? (
              <div className="text-center">
                <CheckCircleIcon className="w-16 h-16 text-go mx-auto mb-4" />
                <h1 className="text-2xl sm:text-3xl font-bold text-default mb-2">
                  Feedback submitted!
                </h1>
                <p className="text-sm text-subtext mb-6">
                  {formData.contactMethod !== "none"
                    ? "We'll contact you via your preferred method soon."
                    : "Thank you for helping us improve our services."}
                </p>
                <Link to="/">
                  <Button fill title="Go to Home" />
                </Link>
              </div>
            ) : (
              <>
                <div className="text-center lg:text-left mb-7">
                  <h1 className="text-2xl sm:text-3xl font-bold text-default mb-2">
                    Tap n' Go Customer Feedback
                  </h1>
                  <p className="text-sm text-subtext">Help us improve our services</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  {error && (
                    <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                      <span>{error}</span>
                    </div>
                  )}

                  <div>
                    <label className={labelClass}>Type of Service *</label>
                    <select
                      name="serviceType"
                      value={formData.serviceType}
                      onChange={handleChange}
                      className={inputClass}
                      required
                    >
                      <option value="">Select a service</option>
                      <option value="intercity">Intercity</option>
                      <option value="innercity">Inner City (EV Bus, Trotro, Taxi)</option>
                      <option value="railways">Railways</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  {formData.serviceType === "other" && (
                    <div>
                      <label className={labelClass}>Please specify the service</label>
                      <input
                        type="text"
                        name="otherService"
                        value={formData.otherService}
                        onChange={handleChange}
                        className={inputClass}
                        required
                      />
                    </div>
                  )}

                  <div>
                    <label className={labelClass}>Complaint Category *</label>
                    <select
                      name="complaintCategory"
                      value={formData.complaintCategory}
                      onChange={handleChange}
                      className={inputClass}
                      required
                    >
                      <option value="">Select category</option>
                      {Object.entries(complaintCategories).map(([key, category]) => (
                        <option key={key} value={key}>
                          {category.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {formData.complaintCategory && (
                    <div>
                      <label className={labelClass}>Specific Issue *</label>
                      <select
                        name="complaintType"
                        value={formData.complaintType}
                        onChange={handleChange}
                        className={inputClass}
                        required
                      >
                        <option value="">Select specific issue</option>
                        {complaintCategories[formData.complaintCategory].options.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {formData.complaintType === "Other" && (
                    <div>
                      <label className={labelClass}>Please specify your issue</label>
                      <input
                        type="text"
                        name="otherComplaint"
                        value={formData.otherComplaint}
                        onChange={handleChange}
                        className={inputClass}
                        required
                      />
                    </div>
                  )}

                  <div>
                    <label className={labelClass}>Time of Travel *</label>
                    <input
                      type="datetime-local"
                      name="travelTime"
                      value={formData.travelTime}
                      onChange={handleChange}
                      className={inputClass}
                      required
                    />
                  </div>

                  <div>
                    <label className={labelClass}>From *</label>
                    <SearchableDropdown value={from} onChange={setFrom} />
                  </div>
                  <div>
                    <label className={labelClass}>To *</label>
                    <SearchableDropdown value={to} onChange={setTo} />
                  </div>

                  {formData.route === "Other" && (
                    <div>
                      <label className={labelClass}>Please specify your route</label>
                      <input
                        type="text"
                        name="otherRoute"
                        value={formData.otherRoute}
                        onChange={handleChange}
                        className={inputClass}
                        required
                      />
                    </div>
                  )}

                  <div>
                    <label className={labelClass}>Preferred Contact Method *</label>
                    <div className="grid grid-cols-2 gap-3">
                      {["Email", "Phone Call", "WhatsApp", "No Contact Needed"].map((method) => (
                        <label
                          key={method}
                          className="flex items-center rounded-lg border border-secondary px-3 py-3 cursor-pointer"
                        >
                          <input
                            type="radio"
                            name="contactMethod"
                            value={method.toLowerCase()}
                            checked={formData.contactMethod === method.toLowerCase()}
                            onChange={handleChange}
                            className="w-4 h-4 text-green-700 border-gray-300 focus:ring-go"
                            required
                          />
                          <span className="ml-2 text-sm text-default">{method}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {formData.contactMethod && formData.contactMethod !== "no contact needed" && (
                    <div>
                      <label className={labelClass}>Contact Details *</label>
                      <input
                        type={formData.contactMethod === "email" ? "email" : "tel"}
                        name="contactDetails"
                        value={formData.contactDetails}
                        onChange={handleChange}
                        placeholder={`Enter your ${formData.contactMethod}`}
                        className={inputClass}
                        required
                      />
                    </div>
                  )}

                  <p className="text-xs text-subtext bg-secondary-surface p-3 rounded-lg border border-secondary">
                    <strong>Privacy Notice:</strong> Your contact details will only be used to resolve your reported issue.
                    Tap n' Go will not share your information with third parties.
                  </p>

                  <Button fill title="Submit Feedback" loading={isLoading} disabled={isLoading} type="submit" />
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedbackForm;
